import { useState } from 'react';
import Card from 'components/card';
import { MdPersonAdd, MdSearch, MdPerson, MdBusiness, MdLocationOn, MdLink, MdCancel, MdCheckCircle } from 'react-icons/md';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@config/firebase';
import { useTenant } from '@contexts/TenantContext';
import notificationService from '@services/notificationService';
import ConfirmationModal from 'components/modal/ConfirmationModal';

interface ProfileResult {
  name: string;
  position: string;
  company: string;
  location: string;
  education?: string;
  profileUrl?: string;
  email?: string;
}

interface SearchDebug {
  search_query: string;
  profiles_found: number;
  selection_criteria: string;
}

interface SearchResponse {
  success: boolean;
  profiles: ProfileResult[];
  debug: SearchDebug;
  searchedName?: string;
}

const AddUser = () => {
  const { currentUser } = useTenant();
  const [searchData, setSearchData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    keyPoints: [''] // Start with one empty key point
  });

  const [searchResults, setSearchResults] = useState<ProfileResult[]>([]);
  const [searchDebug, setSearchDebug] = useState<SearchDebug | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<number | null>(null);
  const [isCreatingUsers, setIsCreatingUsers] = useState(false);
  const [searchError, setSearchError] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdUserName, setCreatedUserName] = useState<string>('');

  const handleSearchInputChange = (field: string, value: string) => {
    setSearchData(prev => ({
      ...prev,
      [field]: value
    }));
    setSearchError('');
  };

  const handleKeyPointChange = (index: number, value: string) => {
    const newKeyPoints = [...searchData.keyPoints];
    newKeyPoints[index] = value;
    setSearchData(prev => ({
      ...prev,
      keyPoints: newKeyPoints
    }));
  };

  const addKeyPoint = () => {
    if (searchData.keyPoints.length < 5) {
      setSearchData(prev => ({
        ...prev,
        keyPoints: [...prev.keyPoints, '']
      }));
    }
  };

  const removeKeyPoint = (index: number) => {
    if (searchData.keyPoints.length > 1) {
      setSearchData(prev => ({
        ...prev,
        keyPoints: prev.keyPoints.filter((_, i) => i !== index)
      }));
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const searchProfilesFunc = async () => {
    if (!searchData.firstName.trim() || !searchData.lastName.trim()) {
      setSearchError('First name and last name are required');
      return;
    }

    if (!searchData.email.trim()) {
      setSearchError('Email address is required');
      return;
    }

    if (!validateEmail(searchData.email.trim())) {
      setSearchError('Please enter a valid email address');
      return;
    }

    if (!currentUser) {
      setSearchError('You must be logged in to search profiles');
      return;
    }

    setIsSearching(true);
    setSearchError('');

    try {
      const webSearchProfile = httpsCallable(functions, 'webSearchProfile');

      // Filter out empty key points
      const filteredKeyPoints = searchData.keyPoints.filter(point => point.trim());

      const requestData = {
        firstName: searchData.firstName.trim(),
        lastName: searchData.lastName.trim(),
        ...(filteredKeyPoints.length > 0 && { keyPoints: filteredKeyPoints })
      };


      const result = await webSearchProfile(requestData);

      // Handle malformed response with multiple JSON objects
      let responseData = result.data as SearchResponse;

      // If response is a string with multiple JSON objects, parse the last one
      if (typeof result.data === 'string') {
        const jsonMatches = result.data.match(/\{[^{}]*\}/g);
        if (jsonMatches && jsonMatches.length > 0) {
          // Use the last valid JSON object (usually has the actual results)
          responseData = JSON.parse(jsonMatches[jsonMatches.length - 1]);
        }
      }

      if (responseData.success) {
        // Add email to each profile result
        const profilesWithEmail = (responseData.profiles || []).map(profile => ({
          ...profile,
          email: searchData.email.trim()
        }));
        setSearchResults(profilesWithEmail);
        setSearchDebug(responseData.debug || null);
        setSelectedProfile(null);
      } else {
        throw new Error('Search was not successful');
      }
    } catch (error: any) {
      console.error('Search error:', error);
      setSearchError(`Failed to search profiles: ${error.message || 'Unknown error'}`);
      setSearchResults([]);
      setSearchDebug(null);
    } finally {
      setIsSearching(false);
    }
  };

  const selectProfile = (index: number) => {
    setSelectedProfile(selectedProfile === index ? null : index);
  };

  const createUserFromProfile = async () => {
    if (selectedProfile === null) {
      notificationService.warning('Please select a profile to create a user.');
      return;
    }

    setIsCreatingUsers(true);

    try {
      // Get selected profile
      const profileToCreate = searchResults[selectedProfile];

      // Simulate API call to create user
      setTimeout(() => {
        setCreatedUserName(profileToCreate.name);
        setShowSuccessModal(true);

        setIsCreatingUsers(false);
      }, 2000);
    } catch (error: any) {
      console.error('Error creating user:', error);
      notificationService.error(`Failed to create user account: ${error.message || 'Unknown error'}`);
      setIsCreatingUsers(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    // Reset after successful creation
    setSearchResults([]);
    setSelectedProfile(null);
    setSearchData({
      firstName: '',
      lastName: '',
      email: '',
      keyPoints: ['']
    });
    setSearchDebug(null);
    setCreatedUserName('');
  };

  const handleReset = () => {
    setSearchData({
      firstName: '',
      lastName: '',
      email: '',
      keyPoints: ['']
    });
    setSearchResults([]);
    setSelectedProfile(null);
    setSearchError('');
    setSearchDebug(null);
  };

  return (
    <div className="mt-3 h-full w-full">
      {/* Coming Soon Banner */}
      <div className="mb-4 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 font-medium">
            Coming Soon
          </span>
          <p className="text-sm text-yellow-700 dark:text-yellow-400">
            This feature is currently under development and will be available soon.
          </p>
        </div>
      </div>
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
      <div className="mx-auto h-full w-11/12 lg:w-10/12 xl:w-9/12 md:px-3">
        <div className="-mt-[280px] w-full pb-10 md:-mt-[240px] flex gap-6">
          {/* Main Card */}
          <div className="flex-1">
            <Card extra={'h-full pb-3 shadow-lg'}>
              <div className="rounded-[20px] px-3 pt-7 md:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                      Add User via Search
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Search for professional profiles and create user accounts
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={searchProfilesFunc}
                      disabled={isSearching}
                      className={`flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-medium transition-all ${isSearching
                        ? "bg-gray-500 text-white cursor-not-allowed dark:bg-gray-400 dark:text-gray-900"
                        : "bg-brand-500 text-white hover:bg-brand-600 shadow-lg hover:shadow-xl dark:bg-brand-600 dark:hover:bg-brand-700"
                        }`}
                    >
                      <MdSearch className="h-4 w-4" />
                      {isSearching ? "Searching..." : "Search"}
                    </button>

                    <button
                      onClick={handleReset}
                      disabled={isSearching}
                      className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${isSearching
                        ? "border-gray-600 bg-gray-400 text-gray-900 cursor-not-allowed dark:border-gray-500 dark:bg-gray-700 dark:text-white"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-400 dark:text-gray-100 dark:hover:bg-gray-700"
                        }`}
                    >
                      <MdCancel className="h-4 w-4" />
                      Reset
                    </button>
                  </div>
                </div>

                {/* Search Form */}
                <div className="space-y-6">
                  <div className="space-y-4">
                    {/* Name Fields Row */}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label htmlFor="firstName" className="text-sm font-medium text-navy-700 dark:text-white block mb-1">
                          First Name*
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          placeholder="First name"
                          value={searchData.firstName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchInputChange('firstName', e.target.value)}
                          className={`h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-brand-500 ${searchError && !searchData.firstName.trim()
                            ? "border-red-500 text-red-500 placeholder:text-red-500"
                            : "border-gray-200 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
                            }`}
                        />
                      </div>

                      <div>
                        <label htmlFor="lastName" className="text-sm font-medium text-navy-700 dark:text-white block mb-1">
                          Last Name*
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          placeholder="Last name"
                          value={searchData.lastName}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchInputChange('lastName', e.target.value)}
                          className={`h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-brand-500 ${searchError && !searchData.lastName.trim()
                            ? "border-red-500 text-red-500 placeholder:text-red-500"
                            : "border-gray-200 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
                            }`}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="text-sm font-medium text-navy-700 dark:text-white block mb-1">
                        Email Address*
                      </label>
                      <input
                        type="email"
                        id="email"
                        placeholder="email@example.com"
                        value={searchData.email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchInputChange('email', e.target.value)}
                        className={`h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-brand-500 ${searchError && (!searchData.email.trim() || !validateEmail(searchData.email.trim()))
                          ? "border-red-500 text-red-500 placeholder:text-red-500"
                          : "border-gray-200 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
                          }`}
                      />
                    </div>

                    {/* Context Section */}
                    <div>
                      <label className="text-sm font-medium text-navy-700 dark:text-white block mb-2">
                        Context (Optional)
                      </label>

                      <div className="flex flex-wrap gap-2 mb-2">
                        {searchData.keyPoints.map((point, index) => (
                          <div key={index} className="relative">
                            <input
                              type="text"
                              placeholder="Add context..."
                              value={point}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleKeyPointChange(index, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && point.trim() && searchData.keyPoints.length < 5) {
                                  addKeyPoint();
                                }
                              }}
                              className="h-8 px-3 pr-8 text-sm rounded-full border border-gray-200 bg-white outline-none focus:border-brand-500 dark:border-gray-600 dark:bg-navy-800 dark:text-white min-w-[120px]"
                            />
                            {point.trim() && (
                              <button
                                onClick={() => removeKeyPoint(index)}
                                className="absolute right-1 top-1 h-6 w-6 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <MdCancel className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                        {searchData.keyPoints.length < 5 && (
                          <button
                            onClick={addKeyPoint}
                            className="h-8 px-3 text-sm rounded-full border border-dashed border-gray-300 text-gray-500 hover:border-brand-500 hover:text-brand-500 dark:border-gray-600 dark:text-gray-400 dark:hover:border-brand-400 dark:hover:text-brand-400"
                          >
                            + Add
                          </button>
                        )}
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Company, role, location, or school
                      </p>
                    </div>


                    {/* Error Message */}
                    {searchError && (
                      <p className="text-xs text-red-500">{searchError}</p>
                    )}
                  </div>

                  {isSearching && (
                    <div className="flex flex-col items-center justify-center py-12 border border-gray-200 dark:border-gray-600 rounded-lg">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                        <MdSearch className="h-8 w-8 text-gray-400" />
                      </div>
                      <h6 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                        Searching
                        <span className="flex gap-1">
                          <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                          <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                        </span>
                      </h6>
                      <p className="text-sm text-gray-500 dark:text-gray-500 text-center max-w-sm">
                        Looking for "{searchData.firstName} {searchData.lastName}" ({searchData.email})
                      </p>
                    </div>
                  )}

                  {/* Search Results */}
                  {(searchResults.length > 0 || (searchResults.length === 0 && !isSearching && searchData.firstName && searchData.lastName)) && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h5 className="text-lg font-semibold text-navy-700 dark:text-white">
                            Search Results ({searchResults.length} found)
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Select the correct profiles to create user accounts
                          </p>
                        </div>
                        {selectedProfile !== null && (
                          <button
                            onClick={createUserFromProfile}
                            disabled={isCreatingUsers}
                            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${isCreatingUsers
                              ? "bg-gray-500 text-white cursor-not-allowed dark:bg-gray-400 dark:text-gray-900"
                              : "bg-green-500 text-white hover:bg-green-600 shadow-md hover:shadow-lg dark:bg-green-600 dark:hover:bg-green-700"
                              }`}
                          >
                            <MdPersonAdd className="h-4 w-4" />
                            {isCreatingUsers
                              ? "Creating User..."
                              : "Create User"}
                          </button>
                        )}
                      </div>

                      {/* Selection Status */}
                      <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {selectedProfile === null
                              ? "No profile selected - click on a profile to select it"
                              : `Profile "${searchResults[selectedProfile]?.name}" selected for user creation`
                            }
                          </span>
                          {selectedProfile !== null && (
                            <button
                              onClick={() => setSelectedProfile(null)}
                              className="text-xs text-red-500 hover:text-red-600"
                            >
                              Clear Selection
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Debug Info */}
                      {searchDebug && (
                        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                            <strong>Search Query:</strong> {searchDebug.search_query}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            <strong>Selection Criteria:</strong> {searchDebug.selection_criteria}
                          </p>
                        </div>
                      )}

                      {/* Profile Results */}
                      {searchResults.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 border border-gray-200 dark:border-gray-600 rounded-lg">
                          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                            <MdSearch className="h-8 w-8 text-gray-400" />
                          </div>
                          <h6 className="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">
                            No Profiles Found
                          </h6>
                          <p className="text-sm text-gray-500 dark:text-gray-500 text-center max-w-sm">
                            No matching profiles were found for "{searchData.firstName} {searchData.lastName}".
                            Try adjusting your search terms or adding more context.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {searchResults.map((profile, index) => (
                            <div
                              key={index}
                              className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${selectedProfile === index
                                ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-500'
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                              onClick={() => selectProfile(index)}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <MdPerson className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                  <h6 className="font-semibold text-navy-700 dark:text-white">
                                    {profile.name}
                                  </h6>
                                </div>
                                <div className="space-y-1">
                                  {profile.position && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                      <MdBusiness className="h-4 w-4" />
                                      <span>{profile.position}</span>
                                      {profile.company && <span>at {profile.company}</span>}
                                    </div>
                                  )}
                                  {profile.location && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                      <MdLocationOn className="h-4 w-4" />
                                      <span>{profile.location}</span>
                                    </div>
                                  )}
                                  {profile.profileUrl && (
                                    <div className="flex items-center gap-2 text-sm text-brand-500">
                                      <MdLink className="h-4 w-4" />
                                      <a
                                        href={profile.profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        View Profile
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedProfile === index
                                ? 'bg-brand-500 border-brand-500'
                                : 'border-gray-300 dark:border-gray-600'
                                }`}>
                                {selectedProfile === index && (
                                  <div className="w-2 h-2 bg-white rounded-full" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
            </Card>
          </div>

          {/* Floating Information Panel */}
          <div className="hidden xl:block w-80">
            <div className="sticky top-24">
              <Card extra="p-6 shadow-lg">
                <div>
                  <h6 className="font-semibold text-navy-700 dark:text-white mb-2">
                    Profile Search & User Creation
                  </h6>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Search for professional profiles using AI. Select a profile to create a user account.
                    The user account will be created with the profile information as a starting point.
                  </p>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-brand-500 font-bold">1.</span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Search</span> - Enter name + email + context
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-brand-500 font-bold">2.</span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Find</span> - AI locates profiles
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-brand-500 font-bold">3.</span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Select</span> - Choose correct matches
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-brand-500 font-bold">4.</span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Create</span> - Generate user accounts
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

        </div>
      </div>

      <ConfirmationModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        title="Coming Soon!"
        message={
          <div className="space-y-2">
            <p>
              User creation for <span className="font-semibold text-navy-700 dark:text-white">{createdUserName}</span> is not yet available.
            </p>
            <p className="text-sm">
              This feature will be ready soon.
            </p>
          </div>
        }
        icon={<MdCheckCircle className="h-12 w-12 text-blue-500 dark:text-blue-400" />}
        variant="info"
        confirmText="Got it!"
      />
    </div>
  );
};

export default AddUser;